import { FC, useMemo, useState } from 'react';
import { PlainButton, Typography } from 'omakase';
import {
  SafetyMeasureCategoryType,
  safetyMeasureCategoryNameFromType,
  safetyMeasureIconFromTypeAndSize,
} from './safetyMeasuresUtils';
import { SafetyMeasuresDialog } from './SafetyMeasuresDialog';
import {
  cardIconDesktopStyle,
  cardIconMobileStyle,
  cardRootStyle,
  cardSubTitleStyle,
  cardTitleStyle,
} from './safetyMeasuresCssDefs';
import { getBusinessTagsByMetaTagType } from 'utils/graphQLQueryUtils';
import { gql } from '@apollo/client';
import { useActions } from '@tock/shared/utils/connect';
import { useSafetyMeasuresForCurrentBusinessQuery } from 'generated/graphql';
import LoadingComponent from '@tock/shared/components/LoadingComponent';

interface Props {
  /**
   * Name of the business
   */
  businessName: string;
}

/**
 * Queries for safety measures by business domain
 * Adding the "@cache" directive is reliant on https://github.com/tocktix/admin/pull/16466 being merged to production
 */
export const SAFETY_MEASURES_FOR_CURRENT_BUSINESS = gql`
  query SafetyMeasuresForCurrentBusiness @cache(maxAgeSecs: 60) {
    currentBusiness {
      id
      showCovidSafetyMeasures
      businessTags {
        id
        ...businessTagsFields
      }
    }
  }

  fragment businessTagsFields on BusinessTagEntity {
    id
    metaTag {
      id
      name
      type
    }
  }
`;

export const SafetyMeasuresCard: FC<Props> = ({ businessName }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    actions: { toast },
  } = useActions();

  const { data, loading } = useSafetyMeasuresForCurrentBusinessQuery({
    onError: () => toast.showError('Issue fetching safety measures'),
  });

  const businessTags = data?.currentBusiness?.businessTags;

  const measuresByCategory = useMemo(
    () =>
      businessTags
        ? getBusinessTagsByMetaTagType<SafetyMeasureCategoryType>(businessTags, [
            'COVID_SAFETY_MEASURE_SCREENING',
            'COVID_SAFETY_MEASURE_PPE',
            'COVID_SAFETY_MEASURE_DISTANCING',
            'COVID_SAFETY_MEASURE_SANITIZING',
          ])
        : null,
    [businessTags]
  );

  if (
    !measuresByCategory?.reduce<number>(
      (previous, current) => previous + current.measures.length,
      0
    ) ||
    !data?.currentBusiness?.showCovidSafetyMeasures
  ) {
    return null;
  }

  return (
    <div css={cardRootStyle}>
      <LoadingComponent isLoading={loading}>
        <SafetyMeasuresDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          measuresByCategory={measuresByCategory}
        />
        <div css={cardTitleStyle}>
          <Typography color="fgDefault" mb={0.5} variant="heading04">
            Safety measures
          </Typography>
          <Typography css={cardSubTitleStyle} color="fgDefault" variant="body02">
            {businessName} is helping guests and staff stay safe.&nbsp;{' '}
          </Typography>
          <PlainButton
            variant="primary"
            size="medium"
            aria-label="Show safety measures details"
            onClick={() => setDialogOpen(true)}
          >
            See&nbsp;details
          </PlainButton>
        </div>
        <ul>
          {measuresByCategory.map(({ type, measures }) =>
            measures.length ? (
              <li key={type}>
                <div css={cardIconMobileStyle}>
                  {safetyMeasureIconFromTypeAndSize(type, 'small')}
                </div>
                <div css={cardIconDesktopStyle}>
                  {safetyMeasureIconFromTypeAndSize(type, 'large')}
                </div>
                <Typography color="fgDefault" variant="body02Strong">
                  {safetyMeasureCategoryNameFromType(type)}
                </Typography>
              </li>
            ) : null
          )}
        </ul>
      </LoadingComponent>
    </div>
  );
};
